import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import AssistService from "../../service/AssistService";
import { AssistDetailResp } from "domain/payload/response/assist/AssistDetailResp";
import { useNavPillsIndicator } from "hooks/useNavPillsIndicator";
import CustomDropdown from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";

const ChangePhoneActivation: React.FC = () => {
  const assistService = AssistService();
  const { profile } = useSelector((state: any) => state.auth);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"check" | "history">("check");

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = useState(false);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<AssistDetailResp | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [currentDecision, setCurrentDecision] = useState<"approve" | "reject" | null>(null);

  const { navRef: tabNavRef, indicatorStyle: tabIndicatorStyle } = useNavPillsIndicator(activeTab);

  const handleWhatsAppClick = (phoneNumber: string) => {
    if (!phoneNumber) return;

    const cleanNumber = phoneNumber.replace(/\D/g, '');
    let whatsappNumber = cleanNumber;

    if (whatsappNumber.startsWith('0')) {
      whatsappNumber = '62' + whatsappNumber.substring(1);
    }

    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  };

  const fetchAssistData = useCallback(async () => {
    if (!profile?.data?.token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = profile.data.token;
      const res = await assistService.assistChangePhoneList(
        token,
        currentPage,
        pageSize,
        searchTerm || null,
        activeTab === "check" ? "check" : "history"
      );

      if (res.status === "T" && Array.isArray(res.data)) {
        setData(res.data);
        setTotalPages(res.pagination?.totalPage ?? 1);
        setTotalRecords(res.pagination?.totalRecords ?? res.data.length);
      } else {
        setData([]);
        throw new Error(res.message || "Gagal memuat data assist");
      }
    } catch (err: any) {
      console.error("Error fetching assist list:", err);
      const errorMessage = err.message || "Gagal memuat data assist";
      setError(errorMessage);
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
      setIsSearchButtonDisabled(false);
    }
  }, [profile, currentPage, pageSize, activeTab, searchTerm]);

  useEffect(() => {
    fetchAssistData();
  }, [fetchAssistData]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setIsSearchButtonDisabled(true);
    fetchAssistData();
  };

  const handleViewDetail = async (id: string) => {
    if (!profile?.data?.token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setSelectedId(id);
    setShowDetailModal(true);
    setDetailLoading(true);
    setDetailData(null);
    setDecisionError(null);
    setCurrentDecision(null);

    try {
      const token = profile.data.token;
      const res = await assistService.assistDetail(token, id);

      if (res.status === "T") {
        setDetailData(res.data ?? null);
      } else {
        throw new Error(res.message || "Gagal memuat detail");
      }
    } catch (err: any) {
      console.error("Error fetching assist detail:", err);
      const errorMessage = err.message || "Gagal memuat detail";
      setDecisionError(errorMessage);
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDecision = async (status: "approved" | "rejected") => {
    if (!profile?.data?.token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!selectedId) {
      window.alert('ID tidak ditemukan.');
      return;
    }

    setDecisionLoading(true);
    setCurrentDecision(status === "approved" ? "approve" : "reject");
    setDecisionError(null);

    try {
      const token = profile.data.token;
      const res = await assistService.assistDecision(token, selectedId, status);

      if (res.status === "T") {
        window.alert(`Berhasil ${status === "approved" ? "menyetujui" : "menolak"} permintaan`);
        setShowDetailModal(false);
        fetchAssistData();
      } else {
        throw new Error(res.message || `Gagal melakukan ${status}`);
      }
    } catch (err: any) {
      console.error("Error making decision:", err);
      const errorMessage = err.message || `Gagal melakukan ${status}`;
      setDecisionError(errorMessage);
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setDecisionLoading(false);
      setCurrentDecision(null);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setDetailData(null);
    setSelectedId(null);
    setDecisionError(null);
    setCurrentDecision(null);
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex mb-3 align-items-center">
            <h5 style={{ flex: 1 }}>Change Phone Number Activation</h5>
          </div>
        </div>

        <div className="col-12">
          <div className="p-4 bg-white rounded shadow-sm">
            <div
              className="mb-3 d-flex justify-content-between align-items-center flex-nowrap gap-2 overflow-auto"

            >
              <form className="d-flex align-items-center flex-nowrap" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isSearchButtonDisabled}
                  style={{ height: 38 }}
                >
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div ref={tabNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                <div
                  className="nav-pill-indicator"
                  style={tabIndicatorStyle}
                />
                <button
                  className={`h-100 nav-link ${activeTab === "check" ? "active" : ""
                    }`}
                  onClick={() => {
                    setActiveTab("check");
                    setCurrentPage(1);
                  }}
                >
                  Check
                </button>
                <button
                  className={`h-100 nav-link ${activeTab === "history" ? "active" : ""
                    }`}
                  onClick={() => {
                    setActiveTab("history");
                    setCurrentPage(1);
                  }}
                >
                  History
                </button>
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>Job ID</th>
                  <th>Job Name</th>
                  <th>Field User ID</th>
                  <th>Field User Name</th>
                  <th>Field User Phone</th>
                  <th>Phone Existing</th>
                  <th>Phone Changes</th>
                  <th>Submit At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.jobId}</td>
                        <td>{item.jobName}</td>
                        <td>{item.fieldUserId}</td>
                        <td>{item.fieldUserName}</td>
                        <td>{item.fieldUserPhone}</td>
                        <td>{item.ownerPhoneExisting}</td>
                        <td>{item.phoneChanges}</td>
                        <td>{item.createdAt}</td>
                        <td>
                          <button
                            className="btn btn-outline-info btn-small-compact border-0"
                            onClick={() => handleViewDetail(item.id)}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    totalRecords
                  )} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDetailModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content p-4">
              <h5 className="fw-bold mb-4">Result</h5>

              {detailLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : detailData ? (
                <>
                  <div className="row">
                    <div className="col-md-8">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Job ID</label>
                          <p className="mb-0">{detailData.jobId || "-"}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Job Name</label>
                          <p className="mb-0">{detailData.jobName || "-"}</p>
                        </div>
                        <div className="col-md-12 mb-3">
                          <label className="form-label fw-semibold">Job Description</label>
                          <p className="mb-0">{detailData.jobDescription || "-"}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Field User ID</label>
                          <p className="mb-0">{detailData.fieldUserId || "-"}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Field User Name</label>
                          <p className="mb-0">{detailData.fieldUserName || "-"}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Field User Phone</label>
                          {detailData.fieldUserPhone ? (
                            <div
                              className="fw-medium d-flex align-items-center text-decoration-none cursor-pointer"
                              onClick={() => handleWhatsAppClick(detailData.fieldUserPhone)}
                              style={{ cursor: 'pointer' }}
                              title="Click to chat on WhatsApp"
                            >
                              {detailData.fieldUserPhone}
                              <i className="fa-brands fa-whatsapp ms-2 text-success"></i>
                            </div>
                          ) : (
                            <div className="text-muted">No phone number</div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Owner Name</label>
                          <p className="mb-0">{detailData.ownerName || "-"}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Phone Existing</label>
                          {detailData.ownerPhoneExisting ? (
                            <div
                              className="fw-medium d-flex align-items-center text-decoration-none cursor-pointer"
                              onClick={() => handleWhatsAppClick(detailData.ownerPhoneExisting)}
                              style={{ cursor: 'pointer' }}
                              title="Click to chat on WhatsApp"
                            >
                              {detailData.ownerPhoneExisting}
                              <i className="fa-brands fa-whatsapp ms-2 text-success"></i>
                            </div>
                          ) : (
                            <div className="text-muted">No phone number</div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Phone Changes</label>
                          {detailData.phoneChanges ? (
                            <div
                              className="fw-medium d-flex align-items-center text-decoration-none cursor-pointer"
                              onClick={() => handleWhatsAppClick(detailData.phoneChanges)}
                              style={{ cursor: 'pointer' }}
                              title="Click to chat on WhatsApp"
                            >
                              {detailData.phoneChanges}
                              <i className="fa-brands fa-whatsapp ms-2 text-success"></i>
                            </div>
                          ) : (
                            <div className="text-muted">No phone number</div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label fw-semibold">Submit At</label>
                          <p className="mb-0">{detailData.createdAt || "-"}</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-4">
                        <label className="form-label fw-semibold mb-2">Foto Tampak Depan Saat Ini</label>
                        <div className="rounded" style={{ minHeight: '150px' }}>
                          {detailData.storeFrontEvidence ? (
                            <img
                              src={detailData.storeFrontEvidence}
                              alt="Foto Tampak Depan Saat Ini"
                              className="img-fluid rounded w-100"
                            />
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fa-solid fa-image fa-2x mb-2"></i>
                              <p className="mb-0">Gambar tidak tersedia</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label fw-semibold mb-2">Foto Tampak Depan Saat Akuisisi</label>
                        <div className="rounded" style={{ minHeight: '150px' }}>
                          {detailData.storeFrontExisting ? (
                            <img
                              src={detailData.storeFrontExisting}
                              alt="Foto Tampak Depan Saat Ini"
                              className="img-fluid rounded w-100"
                            />
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fa-solid fa-image fa-2x mb-2"></i>
                              <p className="mb-0">Gambar tidak tersedia</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {decisionError && (
                    <div className="alert alert-danger mt-3 mb-0">
                      {decisionError}
                    </div>
                  )}

                  {activeTab === "check" && detailData && !detailLoading && (
                    <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                      {decisionLoading ? (
                        <div className="d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span>
                            {currentDecision === "approve" ? "Approving..." : "Rejecting..."}
                          </span>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleDecision("rejected")}
                            disabled={decisionLoading}
                            style={{ minWidth: '100px' }}
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleDecision("approved")}
                            disabled={decisionLoading}
                            style={{ minWidth: '100px' }}
                          >
                            Approve
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-danger mb-0">Gagal memuat data detail</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePhoneActivation;